// ===========================================
// Rich Text Editor: table menu
// ===========================================
// Quill 2's table module has commands but no toolbar UI. The editor's table
// button opens this: outside a table, a small form that inserts one (rows ×
// columns; a Popover, not a Menu, so Escape and Tab behave and the inputs keep
// their typed digits); inside a table, a menu to add or remove rows and columns,
// or delete the table.

import { useState } from 'react';
import { Box, Button, Divider, Menu, MenuItem, Popover, TextField } from '@mui/material';

export type TableCommand =
  | { type: 'insert'; rows: number; columns: number }
  | { type: 'rowAbove' | 'rowBelow' | 'columnLeft' | 'columnRight' }
  | { type: 'deleteRow' | 'deleteColumn' | 'deleteTable' };

const MAX_ROWS = 20;
const MAX_COLUMNS = 6;

const EDIT_ITEMS: Array<{ type: Exclude<TableCommand['type'], 'insert'>; label: string; divider?: boolean }> = [
  { type: 'rowAbove', label: 'Insert row above' },
  { type: 'rowBelow', label: 'Insert row below' },
  { type: 'columnLeft', label: 'Insert column left' },
  { type: 'columnRight', label: 'Insert column right', divider: true },
  { type: 'deleteRow', label: 'Delete row' },
  { type: 'deleteColumn', label: 'Delete column' },
  { type: 'deleteTable', label: 'Delete table' },
];

function clamp(value: string, max: number): number {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, max);
}

interface TableMenuProps {
  anchorEl: HTMLElement | null;
  /** The cursor was inside a table when the button was pressed. */
  inTable: boolean;
  onClose: () => void;
  onCommand: (command: TableCommand) => void;
}

export default function TableMenu({ anchorEl, inTable, onClose, onCommand }: TableMenuProps) {
  const [rows, setRows] = useState('3');
  const [columns, setColumns] = useState('3');

  const insert = () => onCommand({ type: 'insert', rows: clamp(rows, MAX_ROWS), columns: clamp(columns, MAX_COLUMNS) });

  const open = anchorEl !== null;

  if (inTable) {
    return (
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        {EDIT_ITEMS.flatMap(({ type, label, divider }) => [
          <MenuItem key={type} onClick={() => onCommand({ type })}>
            {label}
          </MenuItem>,
          ...(divider ? [<Divider key={`${type}-divider`} />] : []),
        ])}
      </Menu>
    );
  }

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { role: 'dialog', 'aria-label': 'Insert table' } }}
    >
      <Box
        component="form"
        sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 2 }}
        onSubmit={(e) => {
          // React bubbles a portal's submit to any form around the editor (the page's own).
          e.preventDefault();
          e.stopPropagation();
          insert();
        }}
      >
        <TextField
          label="Rows"
          type="number"
          size="small"
          value={rows}
          onChange={(e) => setRows(e.target.value)}
          inputProps={{ min: 1, max: MAX_ROWS }}
          sx={{ width: 80 }}
          autoFocus
        />
        <TextField
          label="Columns"
          type="number"
          size="small"
          value={columns}
          onChange={(e) => setColumns(e.target.value)}
          inputProps={{ min: 1, max: MAX_COLUMNS }}
          sx={{ width: 90 }}
        />
        <Button type="submit" variant="contained">
          Insert table
        </Button>
      </Box>
    </Popover>
  );
}
